import React, { useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import s from './s.module.less';
import { Alert, Divider, Switch, Spin, Modal, Button } from 'antd';
import StrategyTable from '../StrategyTable';
import ConfirmSaveModal from '../ConfirmSaveModal';
import { BudgetStatus, IAdBudgetDisplay } from 'types/marketing';
import SpecialtyBidOverrides from '../SpecialtyBidOverrides';
import useBudgetInfo from 'hooks/marketing/useBudgetInfo';
import useAdStatus from 'hooks/marketing/useAdStatus';
import { formatUTCTime } from 'utils/marketing';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import PaymentContent from 'components/PaymentContent';
import StripeIcon from 'assets/common/stripe.png';
import useGetOffForProlisting from 'hooks/useGetOffForProlisting';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import { DefaultSpecialtyBid, MentalHealthBid } from 'hooks/marketing/useBudgetInfo';

interface SetupStepRef {
    handleSave: () => void;
    validateForm: () => boolean;
}

interface SetupStepProps {
    onConfirm?: () => void;
}

const SetupStep = forwardRef<SetupStepRef, SetupStepProps>((props, ref) => {
    const { onConfirm } = props;
    const [confirmSaveModalOpen, setConfirmSaveModalOpen] = useState(false);
    const [bidWarningModalOpen, setBidWarningModalOpen] = useState(false);
    const [budgetInfo, budgetStatus, budgetLoading] = useBudgetInfo();
    const [adStatus] = useAdStatus();
    const [strategyData, setStrategyData] = useState<IAdBudgetDisplay>(budgetInfo);
    const [advancedSettings, setAdvancedSettings] = useState(budgetInfo.enableAdvancedSettings || false);
    const [data] = useProviderHomeInfo();
    const [paymentInfoModalOpen, setPaymentInfoModalOpen] = useState(false);
    const [off] = useGetOffForProlisting();
    const isPPAProvider = !!data?.ppaProvider;

    // 验证状态
    const [strategyTableErrors, setStrategyTableErrors] = useState<{ bidError: string; budgetError: string }>({
        bidError: '',
        budgetError: '',
    });
    const [specialtyBidErrors, setSpecialtyBidErrors] = useState<Record<string, string>>({});

    const changeAdvancedSettings = (checked: boolean) => {
        setAdvancedSettings(checked);
        setStrategyData({ ...strategyData, enableAdvancedSettings: checked });
    };

    // 验证表单
    const validateForm = () => {
        const hasStrategyErrors = strategyTableErrors.bidError || strategyTableErrors.budgetError;
        const hasSpecialtyErrors = Object.keys(specialtyBidErrors).length > 0;

        return !hasStrategyErrors && !hasSpecialtyErrors;
    };

    const handleSave = () => {
        if (!validateForm() || disableBudgetChange) {
            return;
        }
        if (
            strategyData.specialtyBids?.some(
                (item) =>
                    (item.specialtyName !== 'Psychiatric & Mental Health' && item.bidAmount < DefaultSpecialtyBid) ||
                    (item.specialtyName === 'Psychiatric & Mental Health' && item.bidAmount < MentalHealthBid),
            )
        ) {
            setBidWarningModalOpen(true);
            return;
        }
        if (isPPAProvider || adStatus.hasJoinedAdSystem) {
            setConfirmSaveModalOpen(true);
        } else {
            setPaymentInfoModalOpen(true);
        }
    };

    // 使用推荐 bid
    const handleUseRecommendedBid = () => {
        const updatedSpecialtyBids = strategyData.specialtyBids?.map((item) => {
            const recommendedBid =
                item.specialtyName === 'Psychiatric & Mental Health' ? MentalHealthBid : DefaultSpecialtyBid;
            if (item.bidAmount < recommendedBid) {
                return { ...item, bidAmount: recommendedBid };
            }
            return item;
        });

        setStrategyData({ ...strategyData, specialtyBids: updatedSpecialtyBids });
        setBidWarningModalOpen(false);

        // 自动继续到下一步
        if (isPPAProvider || adStatus.hasJoinedAdSystem) {
            setConfirmSaveModalOpen(true);
        } else {
            setPaymentInfoModalOpen(true);
        }
    };

    // 保持当前 bid
    const handleKeepCurrentBid = () => {
        setBidWarningModalOpen(false);
        if (isPPAProvider || adStatus.hasJoinedAdSystem) {
            setConfirmSaveModalOpen(true);
        } else {
            setPaymentInfoModalOpen(true);
        }
    };

    useImperativeHandle(ref, () => ({
        handleSave,
        validateForm,
    }));

    useEffect(() => {
        if (budgetInfo) {
            setStrategyData(budgetInfo);
            setAdvancedSettings(budgetInfo.enableAdvancedSettings || false);
        }
    }, [budgetInfo]);

    // 已经设置 bid 24 小时内不能修改预算
    const { disableBudgetChange } = budgetStatus;

    const bannerRender = () => {
        let message = '';

        if (disableBudgetChange) {
            if (budgetStatus.isBudgetNearlyExhausted) {
                message =
                    'You’ve used over 80% of your budget. Increase your budget to stay visible and competitive on Klarity Health. Note: Bid updates are limited to once every 24 hours. Your current bid is already active, and because rankings on Klarity Health aren’t updated in real time, please return after 24 hours to make further changes.';
            } else if (budgetStatus.isCostAllBudget) {
                message = `Your Pro-Listings have been paused since ${formatUTCTime(budgetInfo.updatedAt)} due to insufficient budget. Increase your budget to resume visibility on Klarity Health. Note: Bid updates are limited to once every 24 hours. Your current bid is active, please return later to make changes. `;
            } else {
                message =
                    'Bid updates are limited to once every 24 hours. Your current bid is already active, and because rankings on Klarity Health aren’t updated in real time, please return after 24 hours to make further changes.';
            }
        } else if (budgetStatus.isBudgetNearlyExhausted) {
            message =
                'You’ve used over 80% of your budget. Increase your budget to stay visible and competitive on Klarity Health.';
        } else if (budgetStatus.isCostAllBudget) {
            message = `Your Pro-Listings have been paused since ${formatUTCTime(budgetInfo.updatedAt)} due to insufficient budget. Increase your budget to resume visibility on Klarity Health.`;
        }

        return (
            message && (
                <Alert
                    className={s.budgetBanner}
                    message={message}
                    type="info"
                    showIcon
                    icon={
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.25706 3.09858C9.02167 1.73928 10.9788 1.73928 11.7434 3.09858L17.3237 13.0191C18.0736 14.3523 17.1102 15.9996 15.5805 15.9996H4.4199C2.89025 15.9996 1.92682 14.3523 2.67675 13.0191L8.25706 3.09858ZM11.0001 12.9998C11.0001 13.552 10.5524 13.9998 10.0001 13.9998C9.44784 13.9998 9.00012 13.552 9.00012 12.9998C9.00012 12.4475 9.44784 11.9998 10.0001 11.9998C10.5524 11.9998 11.0001 12.4475 11.0001 12.9998ZM10.0001 4.99976C9.44784 4.99976 9.00012 5.44747 9.00012 5.99976V8.99976C9.00012 9.55204 9.44784 9.99976 10.0001 9.99976C10.5524 9.99976 11.0001 9.55204 11.0001 8.99976V5.99976C11.0001 5.44747 10.5524 4.99976 10.0001 4.99976Z"
                                fill="#FBBF24"
                            />
                        </svg>
                    }
                />
            )
        );
    };

    return (
        <div className={s.content}>
            <h2 className={s.title}>Set up your Pro-Listings campaign</h2>
            <div className={s.desc}>
                We’ve pre-configured your campaign based on your specialty, location, and patient type — optimized for
                visibility and results, so you can start seeing new bookings faster.
            </div>

            {!adStatus.hasJoinedAdSystem && (
                <Alert
                    type="info"
                    className={s.alert}
                    message={
                        <span>
                            🎁 Your ${off} Pro-Listings free credit is active and will cover your first campaign costs.
                        </span>
                    }
                />
            )}

            <div className={s.sectionTitle}>Recommended campaign settings</div>
            <div className={s.desc}>
                Boost your visibility with competitive, data-driven bids tailored to your specialty and patient demand.
            </div>
            <Spin spinning={budgetLoading}>
                {bannerRender()}
                <StrategyTable
                    data={strategyData}
                    onChange={(data) => setStrategyData({ ...strategyData, ...data })}
                    onValidationChange={setStrategyTableErrors}
                    isWarning={budgetStatus.isBudgetNearlyExhausted || budgetStatus.isCostAllBudget}
                    disabled={disableBudgetChange}
                />
                <Divider className="mt-10 mb-0" />
                <div className={s.sectionTitle}>
                    Advanced settings
                    <Switch
                        checked={advancedSettings}
                        onChange={(checked) => changeAdvancedSettings(checked)}
                        disabled={disableBudgetChange}
                    />
                </div>
                <div className={s.desc}>
                    Customize your bids by specialty and state. Once enabled, this will override your general settings
                    above.
                </div>
                {advancedSettings && (
                    <SpecialtyBidOverrides
                        specialtyBids={strategyData.specialtyBids}
                        bidPerBooking={strategyData.generalBidAmount}
                        budgetCap={strategyData.budgetCap || 0}
                        budgetPeriod={strategyData.budgetPeriod}
                        onChange={(data) => setStrategyData({ ...strategyData, specialtyBids: data })}
                        onValidationChange={setSpecialtyBidErrors}
                        disabled={disableBudgetChange}
                    />
                )}
            </Spin>
            <ConfirmSaveModal
                open={confirmSaveModalOpen}
                onCancel={() => setConfirmSaveModalOpen(false)}
                strategyData={strategyData}
                onConfirm={onConfirm}
            />
            <Modal
                open={paymentInfoModalOpen}
                onCancel={() => setPaymentInfoModalOpen(false)}
                footer={null}
                width={560}
                title={
                    <div className="flex items-center gap-2">
                        Add payment information <img src={StripeIcon} alt="stripe" className={s.stripeIcon} />
                    </div>
                }
                destroyOnClose
            >
                <div className={s.paymentInfoDesc}>
                    Enter your payment details to start Pro-Listings. You’ll only be charged when a new patient books
                    through your listing.
                </div>
                <PaymentContent
                    confirmButtonText="Continue to confirmation"
                    onSuccess={() => {
                        setPaymentInfoModalOpen(false);
                        setConfirmSaveModalOpen(true);
                    }}
                    hidePolicy
                    isPPV
                />
            </Modal>
            <Modal
                open={bidWarningModalOpen}
                onCancel={() => setBidWarningModalOpen(false)}
                footer={null}
                width={560}
                centered
                className={s.bidWarningModal}
            >
                <div className={s.bidWarningContent}>
                    <div className={s.warningIconWrapper}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M16.5161 6.19911C18.0453 3.48051 21.9595 3.4805 23.4887 6.19911L34.6493 26.0402C36.1491 28.7066 34.2223 32.0012 31.163 32.0012H8.84176C5.78246 32.0012 3.8556 28.7066 5.35546 26.0402L16.5161 6.19911ZM22.0022 26.0015C22.0022 27.106 21.1068 28.0015 20.0022 28.0015C18.8976 28.0015 18.0022 27.106 18.0022 26.0015C18.0022 24.8969 18.8976 24.0015 20.0022 24.0015C21.1068 24.0015 22.0022 24.8969 22.0022 26.0015ZM20.0022 10.0015C18.8976 10.0015 18.0022 10.8969 18.0022 12.0015V18.0015C18.0022 19.106 18.8976 20.0015 20.0022 20.0015C21.1068 20.0015 22.0022 19.106 22.0022 18.0015V12.0015C22.0022 10.8969 21.1068 10.0015 20.0022 10.0015Z"
                                fill="#FBBF24"
                            />
                        </svg>
                    </div>
                    <h3 className={s.warningTitle}>Your bid is below the recommended range</h3>
                    <div className={s.warningDescription}>
                        <p>
                            Lower bids may reduce your visibility in patient searches. Providers with competitive bids
                            see up to <strong>3X more bookings</strong>. Keep your bid at the recommended level to stay
                            competitive.
                        </p>
                    </div>
                    <div className={s.warningButtons}>
                        <Button type="primary" size="large" block onClick={handleUseRecommendedBid}>
                            Use recommended bid
                        </Button>
                        <Button size="large" block onClick={handleKeepCurrentBid}>
                            Keep current bid
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
});

export default SetupStep;
